/**
 * CalendarService — client API pour le Calendrier de tenues (Mytenue v1.12.190).
 *
 * Endpoints :
 *  - POST   /calendar              création d'entrée
 *  - GET    /calendar?month=YYYY-MM mois entier (filtré par tier history)
 *  - GET    /calendar/:id          détail
 *  - PUT    /calendar/:id          update partiel
 *  - DELETE /calendar/:id
 *  - POST   /calendar/upload-url   presigned PUT pour photo user
 */

const API_URL = 'https://on0ym6si4l.execute-api.eu-west-3.amazonaws.com/prod';

export const CALENDAR_SOURCES = ['looks_ia', 'mes_looks', 'tenue_du_jour', 'manual'];

export const CALENDAR_OCCASIONS = [
  { id: 'travail',   label: 'Travail',   emoji: '💼' },
  { id: 'casual',    label: 'Casual',    emoji: '☕' },
  { id: 'brunch',    label: 'Brunch',    emoji: '🥐' },
  { id: 'soirée',    label: 'Soirée',    emoji: '🥂' },
  { id: 'sport',     label: 'Sport',     emoji: '🏋️' },
  { id: 'cérémonie', label: 'Cérémonie', emoji: '💍' },
  { id: 'autre',     label: 'Autre',     emoji: '✨' },
];

async function jsonFetch(url, options) {
  const res = await fetch(url, options);
  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }
  if (!res.ok) {
    const err = new Error(body?.errors?.[0]?.message || body?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export async function listMonth({ month, authToken }) {
  if (!authToken) throw new Error('authToken requis');
  const m = month || new Date().toISOString().slice(0, 7);
  return jsonFetch(`${API_URL}/calendar?month=${encodeURIComponent(m)}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
}

export async function getEntryDetail({ id, authToken }) {
  return jsonFetch(`${API_URL}/calendar/${id}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
}

export async function createEntry({ payload, authToken }) {
  return jsonFetch(`${API_URL}/calendar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify(payload),
  });
}

export async function updateEntry({ id, patch, authToken }) {
  return jsonFetch(`${API_URL}/calendar/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify(patch),
  });
}

export async function deleteEntry({ id, authToken }) {
  return jsonFetch(`${API_URL}/calendar/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${authToken}` },
  });
}

export async function requestUserPhotoUploadUrl({ date, contentType = 'image/jpeg', authToken }) {
  return jsonFetch(`${API_URL}/calendar/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    body: JSON.stringify({ date, contentType }),
  });
}

export async function uploadUserPhoto({ uploadUrl, fileUri, contentType }) {
  const blob = await (await fetch(fileUri)).blob();
  const r = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType || 'image/jpeg' },
    body: blob,
  });
  if (!r.ok) throw new Error(`PUT S3 ${r.status}`);
  return true;
}

/**
 * Helper haut-niveau : si une photo locale est fournie, l'upload puis crée l'entrée
 * avec user_photo_url renseigné. Sinon crée l'entrée sans photo.
 */
/**
 * v1.12.225 — Récupère les look_id portés dans les N derniers jours.
 * Utilisé par OutfitOfTheDayService pour la pénalité "répétition".
 * Charge les 2 mois courants si N > 30 (rare).
 */
export async function getRecentlyWornLookIds({ days = 7, authToken }) {
  if (!authToken) return [];
  try {
    const now = new Date();
    const months = [now.toISOString().slice(0, 7)];
    if (days > 28) {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      months.push(prev.toISOString().slice(0, 7));
    }
    const cutoff = new Date(now.getTime() - days * 24 * 3600 * 1000);
    const ids = new Set();
    for (const m of months) {
      const r = await listMonth({ month: m, authToken }).catch(() => null);
      const entries = r?.entries || r?.data?.entries || [];
      for (const e of entries) {
        if (!e?.look_id) continue;
        const d = new Date(e.date);
        if (d >= cutoff) ids.add(String(e.look_id));
      }
    }
    return Array.from(ids);
  } catch {
    return [];
  }
}

export async function addEntryWithOptionalPhoto({ payload, photoUri, authToken }) {
  let user_photo_url = null;
  if (photoUri) {
    const { uploadUrl, s3Key, contentType } = await requestUserPhotoUploadUrl({
      date: payload.date,
      contentType: 'image/jpeg',
      authToken,
    });
    await uploadUserPhoto({ uploadUrl, fileUri: photoUri, contentType });
    // L'URL publique sera servie via /wardrobe/image?key=... (signed) ou un endpoint dédié.
    // Pour la phase A, on stocke la s3Key brute (qui pourra être resignée à la lecture).
    const bucket = 'outfit-photos-201064226116';
    user_photo_url = `https://${bucket}.s3.eu-west-3.amazonaws.com/${s3Key}`;
  }
  return createEntry({
    payload: { ...payload, user_photo_url },
    authToken,
  });
}
